// Schema-driven affordances for the value editor, on the schema engine. The schema-engine
// port of value-editor-schema.tsx — same exported surface (SchemaWarningBadge,
// literalOptions, LiteralValueSelect, suggestedFieldType, useSchemaKeyLabel,
// useSchemaFieldSuggestions, SchemaFieldChips, LiteralOption), schema-engine-backed and
// still strictly advisory: without a provider up-tree every hook returns
// nothing and the editor behaves exactly as before.
import {Plus, TriangleAlert} from 'lucide-react'
import {Button} from '../button'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '../select-dropdown'
import {Tooltip} from '../tooltip'
import type {ValuePath} from '../value-editor'
import {seedValue} from './data-editor'
import {type HypermediaSchema, kindOf, literalMembers, structFields} from './engine'
import {subschemaAt, useAllSchemaWarnings, useSchemaContext, useSchemaWarnings} from './schema-context'
import type {SchemaWarning} from './schema-context'

/**
 * Red badge flagging a field whose value is out of spec for the schema, with the problem said
 * inline ("is required", "must be a date (YYYY-MM-DD)") — the same words the summary uses, so a
 * reader fixes the row without hunting for it. Several problems: the first inline, all on hover.
 */
export function SchemaWarningBadge({path}: {path: ValuePath}) {
  const warnings = useSchemaWarnings(path)
  if (warnings.length === 0) return null
  const first = describeWarning(warnings[0]!)
  const more = warnings.length - 1
  return (
    <Tooltip content={warnings.map((warning) => describeWarning(warning).text).join(' · ')}>
      <span className="text-destructive flex min-w-0 shrink items-center gap-1 text-xs" data-testid="schema-warning">
        <TriangleAlert className="size-3.5 shrink-0" />
        <span className="truncate">
          {first.text}
          {more > 0 ? ` (+${more})` : ''}
        </span>
      </span>
    </Tooltip>
  )
}

/** Friendly names for the kinds, for readable validation messages. */
const KIND_LABEL: Record<string, string> = {
  null: 'nothing',
  boolean: 'a toggle',
  integer: 'a whole number',
  float: 'a number',
  string: 'text',
  bytes: 'bytes',
  list: 'a list',
  map: 'an object',
  link: 'a link',
}

/** What a value of a semantic format should look like, for validation messages. */
const FORMAT_HINT: Record<string, string> = {
  date: 'must be a date (YYYY-MM-DD)',
  'date-time': 'must be a date and time (YYYY-MM-DDTHH:MM:SSZ)',
  'hm-url': 'must be an hm:// document URL',
  ipfs: 'must be an ipfs:// reference',
}

/** Turn a raw validate() warning into a field label + a human message. */
function describeWarning(w: SchemaWarning): {field: string | null; text: string} {
  const missing = /^missing required "(.+)"$/.exec(w.message)
  if (missing) return {field: missing[1] ?? null, text: 'is required'}

  const field = w.path.length ? w.path.map(String).join(' › ') : null

  const mismatch = /^expected (\w+), got (\w+)$/.exec(w.message)
  if (mismatch)
    return {
      field,
      text: `must be ${KIND_LABEL[mismatch[1]!] ?? mismatch[1]}, got ${KIND_LABEL[mismatch[2]!] ?? mismatch[2]}`,
    }

  const unexpected = /^unexpected key "(.+)"$/.exec(w.message)
  if (unexpected) return {field: unexpected[1] ?? null, text: 'is not allowed by the schema'}

  const format = /^does not match pattern for format "(.+)"$/.exec(w.message)
  if (format) return {field, text: FORMAT_HINT[format[1]!] ?? `must be a valid ${format[1]}`}

  return {field, text: w.message}
}

/**
 * A red, non-blocking panel listing the ACTUAL schema-validation problems (which
 * field, what's wrong) — so the user can see and fix them. Renders nothing when
 * the data conforms. Guardrail, not a gate — the user can still save.
 */
export function SchemaErrorSummary({className}: {className?: string}) {
  const warnings = useAllSchemaWarnings()
  if (warnings.length === 0) return null
  return (
    <div
      role="alert"
      className={
        'border-destructive/40 bg-destructive/10 text-destructive flex flex-col gap-1.5 rounded-md border px-3 py-2 text-sm ' +
        (className ?? '')
      }
    >
      <div className="flex items-center gap-2 font-medium">
        <TriangleAlert className="size-4 shrink-0" />
        <span>{warnings.length === 1 ? '1 field doesn’t' : `${warnings.length} fields don’t`} match the schema</span>
      </div>
      <ul className="flex flex-col gap-0.5 pl-6">
        {warnings.map((w, i) => {
          const {field, text} = describeWarning(w)
          return (
            <li key={i} className="list-disc">
              {field ? (
                <>
                  <span className="font-mono font-medium">{field}</span> {text}
                </>
              ) : (
                text
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export type LiteralOption = {label: string; value: unknown; description?: string}

const literalLabel = (value: unknown): string => (typeof value === 'string' ? JSON.stringify(value) : String(value))

/**
 * The dropdown options for a literal subschema or a union of literals, or null
 * when it isn't dropdown-safe (a single literal has nothing to choose; an empty
 * or duplicate label). Those keep the free-form inputs and rely on the warning badge.
 */
export function literalOptions(schema: HypermediaSchema): LiteralOption[] | null {
  const members = literalMembers(schema)
  if (!members || members.length < 2) return null
  const options: LiteralOption[] = []
  for (const member of members) {
    const label = literalLabel(member.value)
    if (label === '') return null
    options.push({label, value: member.value, description: member.description})
  }
  if (new Set(options.map((option) => option.label)).size !== options.length) return null
  return options
}

// Escape-hatch sentinel; a NUL prefix can't survive literalLabel (strings are
// JSON-quoted), so no member can shadow it.
const EDIT_AS_TEXT = ' edit-as-text'

/**
 * Select rendered in place of the plain input when the current value is a member
 * of the schema's literal union. The last item is an escape hatch back to free
 * editing, so the schema never removes the ability to type.
 */
export function LiteralValueSelect({
  value,
  options,
  onValue,
  onEditAsText,
}: {
  value: unknown
  options: LiteralOption[]
  onValue: (value: unknown) => void
  onEditAsText: () => void
}) {
  const current = options.find((option) => option.value === value)
  return (
    <Select
      value={current?.label ?? ''}
      onValueChange={(next) => {
        if (next === EDIT_AS_TEXT) return onEditAsText()
        const chosen = options.find((option) => option.label === next)
        if (chosen) onValue(chosen.value)
      }}
    >
      <SelectTrigger className="w-fit min-w-28">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.label} value={option.label}>
            {option.label}
          </SelectItem>
        ))}
        <SelectItem value={EDIT_AS_TEXT} className="text-muted-foreground">
          Custom value…
        </SelectItem>
      </SelectContent>
    </Select>
  )
}

/** The add-form types a schema node can suggest (mirrors NewFieldType values). */
export type SuggestedFieldType = 'text' | 'number' | 'toggle' | 'object' | 'list' | 'null' | 'link' | 'bytes'

/** Map a resolved subschema to the add-field form type it calls for. */
export function suggestedFieldType(schema: HypermediaSchema): SuggestedFieldType | null {
  // Literal unions route through the text form; its value input becomes a
  // dropdown of the options and the commit maps back to the typed literal.
  if (literalOptions(schema)) return 'text'
  switch (schema.type ? kindOf(schema.type) : null) {
    case 'string':
      return 'text'
    case 'integer':
    case 'float':
      return 'number'
    case 'boolean':
      return 'toggle'
    case 'map':
    case 'struct':
      return 'object'
    case 'list':
      return 'list'
    case 'null':
      return 'null'
    case 'link':
      return 'link'
    case 'bytes':
      return 'bytes'
    default:
      return null
  }
}

/**
 * The display label for a field key: a schema-keyed field (`ipfs://<cid>`) shows
 * its schema's name once resolved; anything else returns null and the raw key
 * shows.
 */
export function useSchemaKeyLabel(fieldKey: string): string | null {
  const ctx = useSchemaContext()
  if (!ctx || !fieldKey.startsWith('ipfs://')) return null
  const sub = subschemaAt(ctx.rootSchema, [fieldKey], ctx.registry)
  const name = sub && sub !== 'unresolved' ? sub.name : undefined
  return typeof name === 'string' && name ? name : null
}

export type SchemaFieldSuggestion = {
  key: string
  required: boolean
  schema: HypermediaSchema
  type: SuggestedFieldType | null
}

/**
 * Declared-but-absent properties of the map at `path`, for key suggestions and
 * required-field chips. Empty without a schema.
 */
export function useSchemaFieldSuggestions(path: ValuePath, existingKeys: string[]): SchemaFieldSuggestion[] {
  const ctx = useSchemaContext()
  if (!ctx) return []
  const subschema = subschemaAt(ctx.rootSchema, path, ctx.registry)
  if (!subschema || subschema === 'unresolved' || !subschema.properties) return []
  const suggestions: SchemaFieldSuggestion[] = []
  for (const field of structFields(subschema)) {
    const key = field.name
    if (existingKeys.includes(key)) continue
    // "/" is reserved by DAG-JSON for link/bytes forms; never suggest it.
    if (key === '/') continue
    const resolved = subschemaAt(ctx.rootSchema, [...path, key], ctx.registry)
    if (!resolved || resolved === 'unresolved') continue
    suggestions.push({key, required: field.required, schema: resolved, type: suggestedFieldType(resolved)})
  }
  return suggestions.sort((a, b) => Number(b.required) - Number(a.required))
}

/**
 * One-click chips adding *required* missing fields with seeded starter values.
 * Optional declared fields live in the add form's suggestion row; link/bytes go
 * through the form (a placeholder CID or empty binary can't be fabricated).
 */
export function SchemaFieldChips({
  path,
  existingKeys,
  rules,
  onAdd,
}: {
  path: ValuePath
  existingKeys: string[]
  rules?: {lists: boolean; floats: boolean; ipld: boolean}
  onAdd: (key: string, value: unknown) => void
}) {
  const ctx = useSchemaContext()
  const suggestions = useSchemaFieldSuggestions(path, existingKeys)
  if (!ctx) return null
  const addable = suggestions.filter(
    (suggestion) =>
      suggestion.required &&
      suggestion.type !== null &&
      suggestion.type !== 'link' &&
      suggestion.type !== 'bytes' &&
      (suggestion.type !== 'list' || (rules?.lists ?? true)) &&
      (suggestion.type !== 'number' || (rules?.floats ?? true)),
  )
  if (addable.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1">
      {addable.map(({key, required, schema}) => {
        const label = required ? `${key} (required)` : key
        const chip = (
          <Button
            key={key}
            variant="outline"
            size="sm"
            className="text-muted-foreground h-6 gap-1 px-2 text-xs"
            onClick={() => {
              const starter = seedValue(schema, ctx.registry)
              if (starter !== undefined) onAdd(key, starter)
            }}
          >
            <Plus className="size-3" />
            {label}
          </Button>
        )
        return schema.description ? (
          <Tooltip key={key} content={schema.description} asChild>
            {chip}
          </Tooltip>
        ) : (
          chip
        )
      })}
    </div>
  )
}
